'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { HeaderNav, HeaderNavCurLink } from '@/components/common/HeaderNav';
import { Footer } from '@/components/blocks/Footer';
import { Container } from '@/components/ui/container';
import { Heading } from '@/components/ui/heading';
import { Skeleton } from '@/components/ui/skeleton';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import WidgetTypeSelect, {
  WidgetTypeOption,
} from '@/components/widget/WidgetTypeSelect';
import { WidgetSettingsState } from '@/components/widget/WidgetSettings';
import {
  Nullable,
  WidgetType,
} from '@/components/widget/types';
import {
  generateWidgetURLs,
  generateIframeTemplate,
  isSingleItemGuard,
} from '@/components/widget/utils';
import { SearchData } from '@/components/widget/WidgetSearch';

// Each step is code-split; the skeleton stands in at the same card
// height so the page doesn't jump when the chunk lands.
const StepSkeleton = () => <Skeleton className="h-36 w-full" />;

const DynamicWidgetSearch = dynamic(
  () => import('@/components/widget/WidgetSearch'),
  { loading: StepSkeleton },
);
const DynamicWidgetSettings = dynamic(
  () => import('@/components/widget/WidgetSettings'),
  { loading: StepSkeleton },
);
const DynamicWidgetDemo = dynamic(
  () => import('@/components/widget/WidgetDemo'),
  { loading: StepSkeleton },
);
const DynamicWidgetGeneratorDisclaimer = dynamic(
  () => import('@/components/widget/WidgetGeneratorDisclaimer'),
  { loading: StepSkeleton },
);
const DynamicWidgetCode = dynamic(
  () => import('@/components/widget/WidgetCode'),
  { loading: StepSkeleton },
);

// Order of the guided flow. Steps reveal progressively as the previous
// one is filled in, so the numbers double as a "where am I" cue.
const STEP = {
  type: 1,
  search: 2,
  settings: 3,
  demo: 4,
  disclaimer: 5,
  code: 6,
} as const;

function WidgetGenerator() {
  const widgetHeight = 300;
  const { t } = useOTSTranslation('common');

  const [widgetType, setWidgetType] = useState<Nullable<WidgetTypeOption>>(null);
  const [searchData, setSearchData] = useState<Nullable<SearchData>>(null);
  const [widgetSettings, setWidgetSetting] = useState<Nullable<WidgetSettingsState>>(null);
  const [isAgreed, setAgreement] = useState<boolean>(false);
  const isSingleResort = widgetType?.value === WidgetType.SINGLE_RESORT;
  const isMultiSelect = widgetType?.value === WidgetType.LIST_OF_RESORTS;
  const isRegion = widgetType?.value === WidgetType.SINGLE_REGION;
  const isMultipleChoice = searchData && !isSingleItemGuard(searchData);
  const showSettings = isMultipleChoice
    ? searchData.length !== 0
    : !!searchData;

  const { url, poweredUrl } = generateWidgetURLs(
    widgetType?.value,
    searchData,
    widgetSettings?.filter?.value,
    widgetSettings?.language,
  );

  const iframeTemplate = generateIframeTemplate(
    widgetSettings?.width,
    widgetHeight,
    url,
    poweredUrl,
  );

  useEffect(() => {
    setSearchData(null);
    setWidgetSetting(null);
    setAgreement(false);
  }, [widgetType]);

  return (
    <>
      <HeaderNav current={HeaderNavCurLink.MAIN} />
      <Container as="main" width="narrow" className="py-8 md:py-12">
        <Heading level={1} size="3xl" className="mb-6 md:mb-8">
          {t('widget.snowWidget')}
        </Heading>
        <div className="space-y-4">
          <WidgetTypeSelect
            step={STEP.type}
            value={widgetType}
            setter={setWidgetType}
          />
          {widgetType && (
            <DynamicWidgetSearch
              step={STEP.search}
              setter={setSearchData}
              value={searchData}
              isMulti={isMultiSelect}
              isRegion={isRegion}
              widgetType={widgetType.value}
            />
          )}
          {showSettings && (
            <DynamicWidgetSettings
              step={STEP.settings}
              setter={setWidgetSetting}
              values={widgetSettings}
              isRegion={isRegion}
              isSingleResort={isSingleResort}
            />
          )}
          {!!widgetSettings?.width && !!url && (
            <DynamicWidgetDemo
              step={STEP.demo}
              url={url}
              poweredUrl={poweredUrl}
              width={widgetSettings.width}
              height={widgetHeight}
            />
          )}
          {!!iframeTemplate && (
            <DynamicWidgetGeneratorDisclaimer
              step={STEP.disclaimer}
              onAgree={setAgreement}
              isAgreed={isAgreed}
            />
          )}
          {isAgreed && iframeTemplate && (
            <DynamicWidgetCode step={STEP.code} template={iframeTemplate} />
          )}
        </div>
      </Container>
      <Footer />
    </>
  );
}

export default WidgetGenerator;
